// Pair-wise probability that when criterion A fails, B also fails.
// Sid-scoped; renders an empty state when no single sid resolves.
import React, { useMemo } from 'react'
import { isEmpty, isNil } from 'lodash'
import { useSelector } from 'react-redux'

import { EChart } from '@/components/charts/EChart'
import { Card } from '@/components/cards/Card'

import { CHART } from '@/components/charts/chartPalette'
import { WidgetActionHint } from '../components/WidgetActionHint'
import { usePickedEntity } from '../helpers/cohort'
import { escapeHtml } from '../helpers/format'
import { useEntityClickEvents } from './chartWidget'

export const CriteriaCorrelationHeatmapWidget = () => {
  const matrix = useSelector((state) => state.qaCopilot.data.criteriaCorrelation)
  const populatedSid = useSelector((state) => state.qaCopilot.data.scorecardOverviewSid)
  const loading = useSelector((state) => state.qaCopilot.loading.criteriaCorrelation)
  const picked = usePickedEntity('scorecard')
  const scorecardSid = picked?.value || null
  const multipleSelected = false

  // While a new sid's bundle is fetching, redux still holds the prior
  // sid's matrix -- treat that as "no matrix yet" to avoid stale cells.
  const matrixIsStale = matrix && populatedSid && populatedSid !== scorecardSid
  const effectiveMatrix = matrixIsStale ? null : matrix
  const err = null

  // Axes = union of ids in the co-occurrence pairs. Each point carries
  // its own `drill` payload (click handlers must read p.data).
  const { names, data } = useMemo(() => {
    const cells = effectiveMatrix?.cells || []
    if (isEmpty(cells)) {
      return { names: [], data: [] }
    }
    const nameById = new Map()
    cells.forEach((cell) => {
      if (!nameById.has(cell.criteria_a_id)) {
        nameById.set(cell.criteria_a_id, cell.criteria_a_name)
      }
      if (!nameById.has(cell.criteria_b_id)) {
        nameById.set(cell.criteria_b_id, cell.criteria_b_name)
      }
    })
    const ids = Array.from(nameById.keys())
    // Stable name ordering so the matrix doesn't reshuffle on re-fetch;
    // capped at 12 criteria so labels stay readable.
    ids.sort((idA, idB) => String(nameById.get(idA)).localeCompare(String(nameById.get(idB))))
    const cappedIds = ids.slice(0, 12)
    const idxById = new Map(cappedIds.map((id, i) => [id, i]))
    const heatmap = []
    cells.forEach((cell) => {
      // Skip the diagonal: P(A fails | A fails) is trivially 100% and
      // only adds a line of the darkest cells that drowns out the real
      // signal (this was the "it's all red" complaint).
      if (cell.criteria_a_id === cell.criteria_b_id) {
        return
      }
      const xi = idxById.get(cell.criteria_a_id)
      const yi = idxById.get(cell.criteria_b_id)
      if (isNil(xi) || isNil(yi)) {
        return
      }
      // BE returns probability in [0, 1]; render as %.
      const pct = Math.round((parseFloat(cell.p_b_fails_given_a_fails) || 0) * 100)
      heatmap.push({
        value: [xi, yi, pct, cell.sample_size],
        // Keep the value legible against the sequential ramp: flip to
        // white ink from the ramp midpoint (CHART.BRAND) up, where the
        // fill is dark enough that dark ink loses contrast; dark ink on
        // the lighter lower half.
        label: { color: pct >= 50 ? CHART.TEXT_ON_FILL : CHART.BRAND_TEXT },
        // Drill into the "dependent" criterion (b) -- the one failing
        // as a side-effect when a fails.
        drill: {
          criteria_id: cell.criteria_b_id,
          name: cell.criteria_b_name,
          scorecard_config_sid: scorecardSid,
        },
      })
    })
    return {
      names: cappedIds.map((id) => nameById.get(id)),
      data: heatmap,
    }
  }, [effectiveMatrix, scorecardSid])

  const option = useMemo(() => {
    return {
      tooltip: {
        formatter: (params) => {
          const [xi, yi, pct, sample] = params.data?.value || []
          return `${escapeHtml(names[yi])} fails when ${escapeHtml(names[xi])} fails: ${pct}%<br/>Sample: ${sample} calls where ${escapeHtml(names[xi])} failed<br/><em>Click to drill into ${escapeHtml(names[yi])}</em>`
        },
      },
      // Wider left grid + ellipsizing labels keep long criterion names
      // readable instead of the default cut at the axis edge.
      grid: { left: 180, right: 24, top: 70, bottom: 12 },
      xAxis: {
        type: 'category',
        data: names,
        position: 'top',
        axisLabel: {
          rotate: -35,
          fontSize: 10,
          formatter: (value) => (value.length > 22 ? `${value.slice(0, 20)}…` : value),
        },
        splitArea: { show: true },
      },
      yAxis: {
        type: 'category',
        data: names,
        splitArea: { show: true },
        axisLabel: {
          fontSize: 10,
          formatter: (value) => (value.length > 24 ? `${value.slice(0, 22)}…` : value),
        },
      },
      // visualMap drives the cell colors but its legend UI is hidden --
      // the caption + per-cell % already convey the scale, so the bottom
      // bar was redundant.
      //  - dimension: 2 colors by the probability (value[2]), not the
      //    last data dim (sample_size) that ECharts defaults to.
      //  - Sequential single-hue ramp (light -> dark blurple): a
      //    magnitude, so darker = stronger co-failure.
      visualMap: {
        show: false,
        min: 0,
        max: 100,
        dimension: 2,
        inRange: { color: [CHART.BRAND_BG, CHART.BRAND, CHART.BRAND_TEXT] },
      },
      series: [
        {
          type: 'heatmap',
          data,
          label: { show: true, fontSize: 9, formatter: (params) => `${params.data?.value?.[2]}` },
        },
      ],
    }
  }, [names, data])

  const events = useEntityClickEvents('drill', 'criterion')

  return (
    <Card
      title="Criteria Co-Failure"
      info="When one criterion fails, how often another fails on the same call. High values point to linked issues worth coaching together."
    >
      <WidgetActionHint kind="drawer">Click a cell to drill into the criterion</WidgetActionHint>
      {!scorecardSid && (
        <div className="entity-drawer-empty">
          Pick a scorecard from the filter bar to see criteria correlations.
        </div>
      )}
      {scorecardSid && err && (
        <div className="entity-drawer-empty">
          No correlation data for this scorecard in this window.
        </div>
      )}
      {scorecardSid && !err && !loading && isEmpty(names) && (
        <div className="entity-drawer-empty">
          No co-failures in this window for the selected scorecard.
        </div>
      )}
      {scorecardSid && !err && (loading || !isEmpty(names)) && (
        <>
          {multipleSelected && (
            <div className="widget-hint">
              Showing correlations for the first selected scorecard only.
            </div>
          )}
          <p className="widget-hint">
            When a column criterion fails, how often the row criterion also fails. Darker = more
            often.
          </p>
          <EChart option={option} loading={loading} style={{ height: '380px' }} events={events} />
        </>
      )}
    </Card>
  )
}
